import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Eye, Target, BadgeCheck, Award } from "lucide-react";
import { FadeIn, SectionHeading } from "./Reveal";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1610891015188-5369212db097?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwZmFjdG9yeSUyMG1hbnVmYWN0dXJpbmclMjBtYWNoaW5lcnl8ZW58MHx8fHwxNzkwMjM1OTg5fDA&ixlib=rb-4.1.0&q=85&w=1200&auto=format";

const Counter = ({ to, suffix = "", label }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: "easeOut",
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);

  return (
    <div ref={ref} data-testid="stat-counter" className="border-l-2 border-brand-red pl-5">
      <div className="font-display text-4xl font-semibold text-white sm:text-5xl">
        {val}
        <span className="text-brand-red">{suffix}</span>
      </div>
      <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{label}</p>
    </div>
  );
};

export const About = () => (
  <section id="about" className="relative py-20 sm:py-28 lg:py-36">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionHeading
        kicker="01 — About Us"
        title="A group built on verified, certifiable trust."
        description="New candidates: everything you read here is backed by registrations, standards and a physical presence you can visit."
      />

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
        {/* Image with spotlight / clipped frame */}
        <FadeIn className="lg:col-span-5">
          <div className="group relative">
            <div className="absolute -inset-3 rounded-2xl bg-gradient-to-br from-brand-red/25 via-transparent to-transparent opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-100" />
            <div className="relative overflow-hidden rounded-2xl border border-white/10">
              <img
                src={ABOUT_IMG}
                alt="Inside a Techworld Business Group production facility"
                className="h-[420px] w-full object-cover transition-transform duration-700 group-hover:scale-105 lg:h-[560px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex flex-wrap gap-2">
                <span className="flex items-center gap-2 rounded-full bg-ink/80 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white backdrop-blur-md">
                  <BadgeCheck size={13} className="text-brand-red" /> ISO 9001:2015
                </span>
                <span className="flex items-center gap-2 rounded-full bg-ink/80 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white backdrop-blur-md">
                  <Award size={13} className="text-brand-red" /> FSSAI Licensed
                </span>
              </div>
            </div>
          </div>
        </FadeIn>

        {/* Story, mission, vision */}
        <div className="flex flex-col gap-6 lg:col-span-7">
          <FadeIn delay={0.1}>
            <h3 className="font-display text-2xl font-semibold text-white sm:text-3xl">Our story</h3>
            <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">
              Techworld Business Group is a certified business group focused on
              importing, exporting and manufacturing, headquartered in Alappuzha.
              What has not changed over three decades is how we work: documented
              processes, certified quality systems and long-term relationships with
              the people who make and buy what we move.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">
              Today, we manufacture, distribute and market seven families of everyday
              products — hardware items, medical equipments, pharmaceutical items,
              FMCG items, spices, cosmetic items and kitchen equipments — carrying
              one promise across them all: quality you can verify in every unit
              that leaves our hands.
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="product-categories">
              {["Hardware Items", "Medical Equipments", "Pharmaceutical Items", "FMCG Items", "Spices", "Cosmetic Items", "Kitchen Equipments"].map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-200"
                >
                  {c}
                </span>
              ))}
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <FadeIn delay={0.15} className="h-full">
              <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm transition-all duration-300 hover:border-brand-red/40 sm:p-8">
                <Target className="text-brand-red" size={26} />
                <h4 className="mt-5 font-display text-xl font-semibold text-white">Our mission</h4>
                <ul className="mt-3 flex flex-col gap-2 text-sm leading-relaxed text-slate-400">
                  <li className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-brand-red" />Provide high-quality products and services that exceed customer expectations.</li>
                  <li className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-brand-red" />Uphold the highest standards of quality through certified management systems.</li>
                  <li className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-brand-red" />Foster innovation, sustainability and long-term partnerships worldwide.</li>
                </ul>
              </div>
            </FadeIn>
            <FadeIn delay={0.22} className="h-full">
              <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm transition-all duration-300 hover:border-brand-red/40 sm:p-8">
                <Eye className="text-brand-red" size={26} />
                <h4 className="mt-5 font-display text-xl font-semibold text-white">Our vision</h4>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                  To be a globally recognized leader in quality management,
                  manufacturing and trade — delivering innovative solutions that
                  drive sustainable growth and customer satisfaction.
                </p>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.28}>
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
              <h4 className="font-display text-xl font-semibold text-white">Quality & compliance</h4>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Techworld is an ISO 9001:2015 certified business group with FSSAI
                registration. Certified quality management frameworks govern every
                operation — ensuring superior quality and reliability, not paperwork
                for show.
              </p>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* Counters */}
      <div className="mt-16 grid grid-cols-1 gap-10 sm:grid-cols-3 lg:mt-24">
        <FadeIn><Counter to={30} suffix="+" label="Years of Experience" /></FadeIn>
        <FadeIn delay={0.1}><Counter to={4} label="Business Verticals" /></FadeIn>
        <FadeIn delay={0.2}><Counter to={67} suffix="+" label="Branches Across Kerala" /></FadeIn>
      </div>
    </div>
  </section>
);

export default About;
